import { useQuery } from '@tanstack/react-query'
import { CalendarDays, CheckCircle2, MapPin, UserRound } from 'lucide-react'
import { useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { ApiError, get, post } from '../../api/client'
import type { PublicEvent, PublicRegistrationInput, PublicRegistrationResult } from '../../api/types'
import { Button, ErrorNote, Field, Input, Spinner, Textarea } from '../../components/ui'
import { EVENT_TYPE_LABEL, fmtEventWhen } from '../../lib/format'

export function RegisterPage() {
  const { token = '' } = useParams()
  const [params] = useSearchParams()
  const tracking = params.get('u')
  const q = useQuery({ queryKey: ['public-event', token], queryFn: () => get<PublicEvent>(`/api/public/anmalan/${token}`), retry: false })
  const [done, setDone] = useState<PublicRegistrationResult | null>(null)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [f, setF] = useState({ first_name: '', last_name: '', email: '', phone: '', company: '', dietary: '', notes: '' })
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF((s) => ({ ...s, [k]: e.target.value }))

  if (q.isLoading) return <Spinner />
  if (q.isError) {
    const err = q.error as ApiError
    return (
      <div className="card p-8 text-center">
        <h1 className="text-xl">{err.status === 410 ? 'Anmälan är stängd' : 'Länken är inte giltig'}</h1>
        <p className="mt-2 text-sm text-ink-500">{String(err.message)} Kontakta din rådgivare om du tror att något är fel.</p>
      </div>
    )
  }
  const ev = q.data!

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setPending(true)
    setError(null)
    try {
      const body: PublicRegistrationInput = { ...f, phone: f.phone || undefined, company: f.company || undefined, dietary: f.dietary || undefined, notes: f.notes || undefined, tracking_token: tracking }
      setDone(await post<PublicRegistrationResult>(`/api/public/anmalan/${token}`, body))
    } catch (err) {
      setError(err)
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="card animate-fade-up overflow-hidden">
        <div className="bg-navy-900 px-7 py-7 text-white">
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-white/60">{EVENT_TYPE_LABEL[ev.event_type]} · Inbjudan</div>
          <h1 className="mt-2 text-[clamp(1.4rem,3vw,2rem)] leading-tight text-white">{ev.event_name}</h1>
          <div className="mt-4 space-y-1.5 text-sm text-white/85">
            <div className="flex items-center gap-2"><CalendarDays className="size-4 shrink-0" />{fmtEventWhen(ev.starts_at, ev.ends_at)}</div>
            <div className="flex items-center gap-2"><MapPin className="size-4 shrink-0" />{ev.location}{ev.address ? `, ${ev.address}` : ''}</div>
            {ev.advisor_name && <div className="flex items-center gap-2"><UserRound className="size-4 shrink-0" />Din rådgivare: {ev.advisor_name}</div>}
          </div>
        </div>
        {ev.description && <p className="px-7 py-5 text-sm leading-relaxed text-ink-700">{ev.description}</p>}
      </div>

      {done ? (
        <div className="card animate-scale-in p-8 text-center">
          <CheckCircle2 className="mx-auto size-10 text-success-500 animate-fade-up" style={{ animationDelay: '120ms' }} />
          <h2 className="mt-3 text-xl">Tack, {done.first_name}! Du är anmäld.</h2>
          <p className="mt-2 text-sm text-ink-500">En bekräftelse har skickats till {f.email}. Vi ses den {fmtEventWhen(done.starts_at, null)}.</p>
        </div>
      ) : ev.state !== 'OPEN' ? (
        <div className="card p-8 text-center">
          <h2 className="text-xl">{ev.state === 'FULL' ? 'Eventet är fullbokat' : 'Anmälan är stängd'}</h2>
          <p className="mt-2 text-sm text-ink-500">{ev.state === 'FULL' ? 'Alla platser är tagna. Hör av dig till din rådgivare om du vill stå på reservlista.' : 'Sista anmälningsdag har passerat.'}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="card animate-fade-up space-y-4 p-7" style={{ animationDelay: '80ms' }}>
          <div className="flex items-baseline justify-between">
            <h2 className="text-lg">Anmäl dig</h2>
            {ev.show_spots_left && <span className="text-xs font-medium text-warning-600">Endast {ev.spots_left} platser kvar</span>}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Förnamn"><Input required value={f.first_name} onChange={set('first_name')} autoComplete="given-name" /></Field>
            <Field label="Efternamn"><Input required value={f.last_name} onChange={set('last_name')} autoComplete="family-name" /></Field>
          </div>
          <Field label="E-post"><Input required type="email" value={f.email} onChange={set('email')} autoComplete="email" /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Telefon (valfritt)"><Input value={f.phone} onChange={set('phone')} autoComplete="tel" /></Field>
            <Field label="Företag (valfritt)"><Input value={f.company} onChange={set('company')} autoComplete="organization" /></Field>
          </div>
          {ev.event_type !== 'WEBINAR' && (
            <Field label="Allergier eller specialkost (valfritt)"><Input value={f.dietary} onChange={set('dietary')} /></Field>
          )}
          <Field label="Meddelande (valfritt)"><Textarea value={f.notes} onChange={set('notes')} className="min-h-20" /></Field>
          <ErrorNote error={error} />
          <Button type="submit" variant="primary" size="lg" className="w-full" loading={pending}>Anmäl mig</Button>
          <p className="text-xs leading-relaxed text-ink-500">Genom att anmäla dig godkänner du att Söderberg &amp; Partners behandlar dina uppgifter för att administrera eventet.</p>
        </form>
      )}
    </div>
  )
}
